
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useGetExtraCompetitionConfig } from './useGetExtraCompetitionConfig';
import { useGetCompetitionConsentConfig } from './useGetCompetitionConsentConfig';
import { createDefaultLangObject } from 'shared/components/__ADDONS__/__SAFEAPI__/Defaults';
import { CompetitionConsentNames } from 'shared/modals/competitionLegalsModal/types';
import { useTranslation } from 'react-i18next';
import { INPUT_TYPES } from '@brg/ui';
import { RequiredLabel } from '_competition/components/style';

const checkVideoLinks = (s:string) => {
  const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
  const vkReg = /^(https:\/\/)?(vk.com)/;
  const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  
  const kinescopeReg = /kinescope\.io/i;

  return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s) || kinescopeReg.test(s);
}

const checkCloudLinks = (s: string): boolean => {
  if (!s) return false;

  const lower = s.trim().toLowerCase();
  const dropbox = /(dropboxusercontent|dropbox)\.com\/(scl\/(fi|fo)|s\/|sh\/|home\/|u\/)/;
  const googleDrive = /(drive|docs|spreadsheets|presentation)\.google\.com\/(drive\/folders|file\/d|open|document\/d|spreadsheets\/d|presentation\/d|spreadsheets\/d\/edit|document\/d\/edit|presentation\/d\/edit)/;
  const yandexDisk = /(disk\.(yandex\.ru|yandex\.com|360\.yandex\.ru)|yadi\.sk)\/(d|i\/|public|client)/;

  return (
    dropbox.test(lower) ||
    googleDrive.test(lower) ||
    yandexDisk.test(lower)
  );
};

const checkUrl = (s: string): boolean => {
  return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/?.test(s)
}

export interface FieldConfig {
    type: any;
    label: string;
    placeholder?: string;
    hidden?: boolean;
    name?: string;
    requiredLabel?: boolean;
    bilingual?: boolean;
    checkboxLabel?: string;
    defaultValue?: any;
    options?: any[];
    validation: yup.AnySchema;
  }

export const useGetFieldConfig = (competitionId?: string, projectId?: string, nominationId?: string, isPreRegistration?: boolean, shouldSignConsent?: boolean, consents?: CompetitionConsentNames[]) => {
    const [configLoading, setConfigLoading] = useState(true)
    const { t, i18n: {language} } = useTranslation();
    const extraConfig = useGetExtraCompetitionConfig(competitionId)
    const consentConfig = useGetCompetitionConsentConfig(shouldSignConsent, consents);

    const isVegaCompetition = useMemo(() => {
      return competitionId && competitionId === 'e7da5f11a1284d09b10c50e59e4f338e'
    } , [])

    useEffect(() => {
      if (projectId && nominationId || isPreRegistration && nominationId) {
          setConfigLoading(false)
      }
    } , [projectId, nominationId, isPreRegistration])

    return {
        config: useMemo(() => {
            return {
                projectId: {
                    type: 'text',
                    label: '',
                    placeholder: '',
                    name: 'projectId',
                    hidden: true,
                    value: projectId,
                    validation: yup
                    .string(),
                },
                nominationId: {
                    type: 'text',
                    label: '',
                    placeholder: '',
                    hidden: true,
                    value: nominationId,
                    name: 'nominationId',
                    validation: yup
                    .string(),
                },
              email: {
                type: 'email',
                label: 'email',
                name: 'email',
                placeholder: 'example@domain.com',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.email'))
                  .email(t('competition_edit.form.message.email')),
              },
              birthDate: {
                type: 'date',
                label: 'birth_date',
                name: "birth_date",
                placeholder: undefined,
                requiredLabel: true,
                validation: yup
                  .date()
                  .nullable()    
                  .required(t('competition_edit.form.message.required.required_field'))
                  .typeError(t('competition_edit.form.message.required.not_empty'))
                  .max(new Date(), t('competition_edit.form.message.max_date')),
              },
              fullName: {
                type: 'text',
                label: 'fullName',
                name: 'fullName',
                placeholder: t('competition_edit.form.placeholder.fullName'),
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
                  .matches(
                    /^[a-zA-Zа-яА-ЯёЁ\s-']+$/,
                    t('competition_edit.form.message.only_word')
                  ),

              },
              courseOfStudy: {
                type: 'text',
                label: 'courseOfStudy',
                name: 'courseOfStudy',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(1, t('competition_edit.form.message.min', {count: 1}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )

              },
              profileOfStudy: {
                type: 'text',
                label: 'profileOfStudy',
                name: 'profileOfStudy',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )

              },
              citizenship: {
                type: 'text',
                label: 'citizenship',
                name: 'citizenship',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
                  .matches(
                    /^[a-zA-Zа-яА-ЯёЁ\s-']+$/,
                    t('competition_edit.form.message.only_word')
                  ),

              },
              regNumber: {
                type: 'text',
                label: 'regNumber',
                name: 'regNumber',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
              },
              phone: {
                type: 'phone',
                label: 'phone',
                name: 'phone',
                requiredLabel: true,
                placeholder: '',
                value: '',
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .matches(
                    /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
                    t('competition_edit.form.message.phone')
                  )
                  .min(10, t('competition_edit.form.message.max', { count: 10 }))
                  .max(18, t('competition_edit.form.message.max', { count: 18 })),
              },
              educationPlace: {
                type: 'text',
                label: 'education_place',
                name: 'education_place',
                requiredLabel: true,
                placeholder: '',
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              endYear: {
                type: 'text',
                label: 'endYear',
                name: 'endYear',
                placeholder: '',
                validation: yup
                  .string()
                //   .required('Обязательное поле')
              },
              city: {
                type: 'text',
                label: 'city',
                name: 'city',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .min(2, t('competition_edit.form.message.min', { count: 2 }))
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              country: {
                type: 'text',
                label: 'country',
                name: 'country',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .min(2, t('competition_edit.form.message.min', { count: 2 }))
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              faculty: {
                type: 'text',
                label: 'faculty',
                name: 'faculty',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              videoLink: {
                type: 'text',
                label: 'video_link',
                name: 'videoLink',
                placeholder: '',
                // requiredLabel: true,
                validation: yup
                  .string()
                  // .required('Обязательное поле')
                  .test(
                    'is-video-link',
                    t('competition_edit.form.message.video_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkVideoLinks(trimmed);
                    }
                  ),
              },
              come_agree: {
                type: 'text',
                label: 'come_agree',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              occupation: {
                type: 'text',
                label: 'occupation',
                placeholder: '',
                // requiredLabel: true,
                validation: yup
                  .string()
                  // .required('Обязательное поле')
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              telegram: {
                type: 'text',
                label: 'telegram',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              nominationName: {
                type: 'select',
                label: 'Номинация',
                requiredLabel: true,
                validation: yup
                 .mixed()
                 .required(t('competition_edit.form.message.required.required_field')),
                defaultValue: 'undefined',
                options: [
                  {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Архитектурное решение'
                        },
                        {
                            lang: 'en',
                            name: 'Architectural solution '
                        },

                ],
                    value: 'undefined'
                },
                {
                  text:  [
                      {
                          lang: 'ru',
                          name: 'Генплан'
                      },
                      {
                          lang: 'en',
                          name: 'Master plan'
                      },

              ],
                  value: 'undefined'
              }
            ]
               },
              nominationsList: {
               type: 'select',
               label: 'Номинация',
               requiredLabel: true,
               validation: yup
                .mixed()
                .required(t('competition_edit.form.message.required.required_field')),
               defaultValue: 'undefined',
               options: isVegaCompetition ? [
                  {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Архитектурное решение'
                        },
                        {
                            lang: 'en',
                            name: 'Architectural solution '
                        },

                ],
                    value: 'undefined'
                },
                {
                  text:  [
                      {
                          lang: 'ru',
                          name: 'Генплан'
                      },
                      {
                          lang: 'en',
                          name: 'Master plan'
                      },

              ],
                  value: 'undefined'
              }
            ] :[
                {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Screen I. Игровое кино'
                        },
                        {
                            lang: 'en',
                            name: 'Screen I. Fiction film'
                        },

                ],
                    value: 'undefined'
                },
                {
                    text:   [
                        {
                            lang: 'ru',
                            name: 'Screen II. Документальное кино'
                        },
                        {
                            lang: 'en',
                            name: ' Screen II. Documentary'
                        },

                    ],
                    value: 'undefined'
                },
                {
                    text:   [
                        {
                            lang: 'ru',
                            name: 'Screen III. Экспериментальное видео и видеоарт'
                        },
                        {
                            lang: 'en',
                            name: 'Screen III. Experimental Video & Videoart'
                        },

                    ],
                    value: 'undefined'
                },
                {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Screen IV. Анимация'
                        },
                        {
                            lang: 'en',
                            name: 'Screen IV. Animation'
                        },

                    ],
                    value: 'undefined'
                },
            ]
              },
              typeOfAward: {
                type: 'select',
                label: 'typeOfAward',
                requiredLabel: true,
                validation: yup
                 .mixed()
                 .required(t('competition_edit.form.message.required.required_field')),
                defaultValue: 'undefined',
                options: [
                 {
                     text:  [
                         {
                             lang: 'ru',
                             name: ' Готов(а) принять участие очно, г. Москва'
                         },
                         {
                             lang: 'en',
                             name: 'Ready to participate in person, Moscow'
                         },
 
                 ],
                     value: 'undefined'
                 },
                 {
                     text:   [
                         {
                             lang: 'ru',
                             name: 'Готов(а) принять участие очно, г. Санкт-Петербург'
                         },
                         {
                             lang: 'en',
                             name: ' Ready to participate in person, St. Petersburg'
                         },
 
                     ],
                     value: 'undefined'
                 },
                 {
                     text:   [
                         {
                             lang: 'ru',
                             name: 'Готов(а) принять участие онлайн'
                         },
                         {
                             lang: 'en',
                             name: 'Ready to participate online'
                         },
 
                     ],
                     value: 'undefined'
                 },
             ]
               },
              projectName_ru: {
                type: INPUT_TYPES.textarea,
                label: 'projectName_ru',
                name: 'projectName_ru',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field')),
                    // .max(100, 'Слишком длинное название'),
              },
              projectName_en: {
                type: INPUT_TYPES.textarea,
                label: 'projectName_en',
                name: 'projectName_en',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field')),
                    // .max(100, 'Слишком длинное название'),
              },
              projectYear: {
                type: 'text',
                label: 'projectYear',
                name: 'projectYear',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(/^\d+$/,t('competition_edit.form.message.only_number'))
                    .max(100, t('competition_edit.form.message.max', { count: 4 })),
              },
              timing: {
                type: 'text',
                label: 'timing',
                name: 'timing',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /^[\d\.,:]+$/,
                      t('competition_edit.form.message.duration')
                    )
                    .max(10, t('competition_edit.form.message.max', { count: 10 })),
              },
              createCountry: {
                type: 'text',
                label: 'createCountry',
                name: 'createCountry',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              about_ru: {
                type: INPUT_TYPES.textarea,
                label: 'about_ru',
                name: 'about_ru',
                placeholder: '',
                requiredLabel: true,
                maxLength: 1000,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /\S/,
                      t('competition_edit.form.message.no_space_symbol')
                    )
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              about_en: {
                type: INPUT_TYPES.textarea,
                label: 'about_en',
                name: 'about_en',
                placeholder: '',
                maxLength: 1000,
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /\S/,
                      t('competition_edit.form.message.no_space_symbol')
                    )
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              hasSubtitles: {
                type: 'radio',
                label: 'hasSubtitles',
                name: 'hasSubtitles',
                // checkboxLabel: 'Я принимаю условия использования и политику конфиденциальности',
                // requiredLabel: true,
                validation: yup
                  .boolean(),
                  // .oneOf([true], 'Необходимо согласиться с условиями'),
                  // .required('Это поле обязательно'),
                value: false,
              },
              cloudLink: {
                type: 'text',
                label: 'cloudLink',
                infoText: 'cloud_info',
                name: "cloudLink",
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    .test(
                      'is-cloud-link',
                      t('competition_edit.form.message.cloud_link'),
                      function (value) {
                        if (!value) return true;
                
                        const trimmed = value.trim();
                        return checkCloudLinks(trimmed);
                      }
                    ),
              },

              publishing: {
                type: 'text',
                label: 'publishing',
                name: 'publishing',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              author: {
                type: 'text',
                label: 'author',
                name: 'author',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              editor: {
                type: 'text',
                label: 'editor',
                name: 'editor',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              bookName: {
                type: 'text',
                label: 'bookName',
                name: 'bookName',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              bookYear: {
                type: 'text',
                label: 'bookYear',
                name: 'bookYear',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .test(
                      'bookYear',
                      t('competition_edit.form.message.year'),
                      function (value) {
                        if (!value) return true;
                
                        const trimmed = value.trim();
                        return /^\d{4}$/.test(trimmed)
                      }
                    )
                    .min(4, t('competition_edit.form.message.min', { count: 4 }))
                    .max(4, t('competition_edit.form.message.max', { count: 4 }))
              },
              webSite: {
                type: 'text',
                label: 'webSite',
                name: 'webSite',
                placeholder: 'https://example.com',
                requiredLabel: false,
                validation: yup
                  .string()
                  .test(
                    'is-pdf-link',
                    t('competition_edit.form.message.url_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkUrl(trimmed);
                    }
                  ),
              },
              contactPerson: {
                type: 'text',
                label: 'contactPerson',
                name: 'contactPerson',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              pdfLink: {
                type: 'text',
                label: 'pdfLink',
                name: 'pdfLink',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                  .string()
                  .test(
                    'is-pdf-link',
                    t('competition_edit.form.message.url_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkUrl(trimmed);
                    }
                  ),
              },

              ...extraConfig,
              ...consentConfig
            };
        } , [projectId, extraConfig, consentConfig, nominationId, language, t]),
        loading: configLoading
    }
}
import { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useGetExtraCompetitionConfig } from './useGetExtraCompetitionConfig';
import { useGetCompetitionConsentConfig } from './useGetCompetitionConsentConfig';
import { createDefaultLangObject } from 'shared/components/__ADDONS__/__SAFEAPI__/Defaults';
import { CompetitionConsentNames } from 'shared/modals/competitionLegalsModal/types';
import { useTranslation } from 'react-i18next';
import { INPUT_TYPES } from '@brg/ui';
import { RequiredLabel } from '_competition/components/style';

const checkVideoLinks = (s:string) => {
  const youbeReg = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const vimeoReg = /^(http|https)?:\/\/?(vimeo.com)/;
  const vkReg = /^(https:\/\/)?(vk.com)/;
  const vkVideoReg = /^(https:\/\/)?(vkvideo.ru)/;  
  const kinescopeReg = /kinescope\.io/i;

  return youbeReg.test(s) || vimeoReg.test(s) || vkReg.test(s) || vkVideoReg.test(s) || kinescopeReg.test(s);
}

const checkCloudLinks = (s: string): boolean => {
  if (!s) return false;

  const lower = s.trim().toLowerCase();
  const dropbox = /(dropboxusercontent|dropbox)\.com\/(scl\/(fi|fo)|s\/|sh\/|home\/|u\/)/;
  const googleDrive = /(drive|docs|spreadsheets|presentation)\.google\.com\/(drive\/folders|file\/d|open|document\/d|spreadsheets\/d|presentation\/d|spreadsheets\/d\/edit|document\/d\/edit|presentation\/d\/edit)/;
  const yandexDisk = /(disk\.(yandex\.ru|yandex\.com|360\.yandex\.ru)|yadi\.sk)\/(d|i\/|public|client)/;

  return (
    dropbox.test(lower) ||
    googleDrive.test(lower) ||
    yandexDisk.test(lower)
  );
};

const checkUrl = (s: string): boolean => {
  return /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/?.test(s)
}

export interface FieldConfig {
    type: any;
    label: string;
    placeholder?: string;
    hidden?: boolean;
    name?: string;
    requiredLabel?: boolean;
    bilingual?: boolean;
    checkboxLabel?: string;
    defaultValue?: any;
    options?: any[];
    validation: yup.AnySchema;
  }

export const useGetFieldConfig = (competitionId?: string, projectId?: string, nominationId?: string, isPreRegistration?: boolean, shouldSignConsent?: boolean, consents?: CompetitionConsentNames[]) => {
    const [configLoading, setConfigLoading] = useState(true)
    const { t, i18n: {language} } = useTranslation();
    const extraConfig = useGetExtraCompetitionConfig(competitionId)
    const consentConfig = useGetCompetitionConsentConfig(shouldSignConsent, consents);

    const isVegaCompetition = useMemo(() => {
      return competitionId && competitionId === 'e7da5f11a1284d09b10c50e59e4f338e'
    } , [])

    useEffect(() => {
      if (projectId && nominationId || isPreRegistration && nominationId) {
          setConfigLoading(false)
      }
    } , [projectId, nominationId, isPreRegistration])

    return {
        config: useMemo(() => {
            return {
                projectId: {
                    type: 'text',
                    label: '',
                    placeholder: '',
                    name: 'projectId',
                    hidden: true,
                    value: projectId,
                    validation: yup
                    .string(),
                },
                nominationId: {
                    type: 'text',
                    label: '',
                    placeholder: '',
                    hidden: true,
                    value: nominationId,
                    name: 'nominationId',
                    validation: yup
                    .string(),
                },
              email: {
                type: 'email',
                label: 'email',
                name: 'email',
                placeholder: 'example@domain.com',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.email'))
                  .email(t('competition_edit.form.message.email')),
              },
              birthDate: {
                type: 'date',
                label: 'birth_date',
                name: "birth_date",
                placeholder: undefined,
                requiredLabel: true,
                validation: yup
                  .date()
                  .nullable()    
                  .required(t('competition_edit.form.message.required.required_field'))
                  .typeError(t('competition_edit.form.message.required.not_empty'))
                  .max(new Date(), t('competition_edit.form.message.max_date')),
              },
              fullName: {
                type: 'text',
                label: 'fullName',
                name: 'fullName',
                placeholder: t('competition_edit.form.placeholder.fullName'),
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
                  .matches(
                    /^[a-zA-Zа-яА-ЯёЁ\s-']+$/,
                    t('competition_edit.form.message.only_word')
                  ),

              },
              courseOfStudy: {
                type: 'text',
                label: 'courseOfStudy',
                name: 'courseOfStudy',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(1, t('competition_edit.form.message.min', {count: 1}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )

              },
              profileOfStudy: {
                type: 'text',
                label: 'profileOfStudy',
                name: 'profileOfStudy',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )

              },
              citizenship: {
                type: 'text',
                label: 'citizenship',
                name: 'citizenship',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .min(2, t('competition_edit.form.message.min', {count: 2}))
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
                  .matches(
                    /^[a-zA-Zа-яА-ЯёЁ\s-']+$/,
                    t('competition_edit.form.message.only_word')
                  ),

              },
              regNumber: {
                type: 'text',
                label: 'regNumber',
                name: 'regNumber',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .trim() 
                  .max(100, t('competition_edit.form.message.min', {count: 100}))
                  .matches(
                    /\S/,
                    t('competition_edit.form.message.no_space_symbol')
                  )
              },
              phone: {
                type: 'phone',
                label: 'phone',
                name: 'phone',
                requiredLabel: true,
                placeholder: '',
                value: '',
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .matches(
                    /^\+?\d{1,4}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
                    t('competition_edit.form.message.phone')
                  )
                  .min(10, t('competition_edit.form.message.max', { count: 10 }))
                  .max(18, t('competition_edit.form.message.max', { count: 18 })),
              },
              educationPlace: {
                type: 'text',
                label: 'education_place',
                name: 'education_place',
                requiredLabel: true,
                placeholder: '',
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              endYear: {
                type: 'text',
                label: 'endYear',
                name: 'endYear',
                placeholder: '',
                validation: yup
                  .string()
                //   .required('Обязательное поле')
              },
              city: {
                type: 'text',
                label: 'city',
                name: 'city',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .min(2, t('competition_edit.form.message.min', { count: 2 }))
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              country: {
                type: 'text',
                label: 'country',
                name: 'country',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
                  .min(2, t('competition_edit.form.message.min', { count: 2 }))
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              faculty: {
                type: 'text',
                label: 'faculty',
                name: 'faculty',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              videoLink: {
                type: 'text',
                label: 'video_link',
                name: 'videoLink',
                placeholder: '',
                // requiredLabel: true,
                validation: yup
                  .string()
                  // .required('Обязательное поле')
                  .test(
                    'is-video-link',
                    t('competition_edit.form.message.video_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkVideoLinks(trimmed);
                    }
                  ),
              },
              come_agree: {
                type: 'text',
                label: 'come_agree',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              occupation: {
                type: 'text',
                label: 'occupation',
                placeholder: '',
                // requiredLabel: true,
                validation: yup
                  .string()
                  // .required('Обязательное поле')
                  .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              telegram: {
                type: 'text',
                label: 'telegram',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                  .string()
                  .required(t('competition_edit.form.message.required.required_field'))
              },
              nominationName: {
                type: 'select',
                label: 'Номинация',
                requiredLabel: true,
                validation: yup
                 .mixed()
                 .required(t('competition_edit.form.message.required.required_field')),
                defaultValue: 'undefined',
                options: [
                  {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Архитектурное решение'
                        },
                        {
                            lang: 'en',
                            name: 'Architectural solution '
                        },

                ],
                    value: 'undefined'
                },
                {
                  text:  [
                      {
                          lang: 'ru',
                          name: 'Генплан'
                      },
                      {
                          lang: 'en',
                          name: 'Master plan'
                      },

              ],
                  value: 'undefined'
              }
            ]
               },
              nominationsList: {
               type: 'select',
               label: 'Номинация',
               requiredLabel: true,
               validation: yup
                .mixed()
                .required(t('competition_edit.form.message.required.required_field')),
               defaultValue: 'undefined',
               options: isVegaCompetition ? [
                  {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Архитектурное решение'
                        },
                        {
                            lang: 'en',
                            name: 'Architectural solution '
                        },

                ],
                    value: 'undefined'
                },
                {
                  text:  [
                      {
                          lang: 'ru',
                          name: 'Генплан'
                      },
                      {
                          lang: 'en',
                          name: 'Master plan'
                      },

              ],
                  value: 'undefined'
              }
            ] :[
                {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Screen I. Игровое кино'
                        },
                        {
                            lang: 'en',
                            name: 'Screen I. Fiction film'
                        },

                ],
                    value: 'undefined'
                },
                {
                    text:   [
                        {
                            lang: 'ru',
                            name: 'Screen II. Документальное кино'
                        },
                        {
                            lang: 'en',
                            name: ' Screen II. Documentary'
                        },

                    ],
                    value: 'undefined'
                },
                {
                    text:   [
                        {
                            lang: 'ru',
                            name: 'Screen III. Экспериментальное видео и видеоарт'
                        },
                        {
                            lang: 'en',
                            name: 'Screen III. Experimental Video & Videoart'
                        },

                    ],
                    value: 'undefined'
                },
                {
                    text:  [
                        {
                            lang: 'ru',
                            name: 'Screen IV. Анимация'
                        },
                        {
                            lang: 'en',
                            name: 'Screen IV. Animation'
                        },

                    ],
                    value: 'undefined'
                },
            ]
              },
              typeOfAward: {
                type: 'select',
                label: 'typeOfAward',
                requiredLabel: true,
                validation: yup
                 .mixed()
                 .required(t('competition_edit.form.message.required.required_field')),
                defaultValue: 'undefined',
                options: [
                 {
                     text:  [
                         {
                             lang: 'ru',
                             name: ' Готов(а) принять участие очно, г. Москва'
                         },
                         {
                             lang: 'en',
                             name: 'Ready to participate in person, Moscow'
                         },
 
                 ],
                     value: 'undefined'
                 },
                 {
                     text:   [
                         {
                             lang: 'ru',
                             name: 'Готов(а) принять участие очно, г. Санкт-Петербург'
                         },
                         {
                             lang: 'en',
                             name: ' Ready to participate in person, St. Petersburg'
                         },
 
                     ],
                     value: 'undefined'
                 },
                 {
                     text:   [
                         {
                             lang: 'ru',
                             name: 'Готов(а) принять участие онлайн'
                         },
                         {
                             lang: 'en',
                             name: 'Ready to participate online'
                         },
 
                     ],
                     value: 'undefined'
                 },
             ]
               },
              projectName_ru: {
                type: INPUT_TYPES.textarea,
                label: 'projectName_ru',
                name: 'projectName_ru',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field')),
                    // .max(100, 'Слишком длинное название'),
              },
              projectName_en: {
                type: INPUT_TYPES.textarea,
                label: 'projectName_en',
                name: 'projectName_en',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field')),
                    // .max(100, 'Слишком длинное название'),
              },
              projectYear: {
                type: 'text',
                label: 'projectYear',
                name: 'projectYear',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(/^\d+$/,t('competition_edit.form.message.only_number'))
                    .max(100, t('competition_edit.form.message.max', { count: 4 })),
              },
              timing: {
                type: 'text',
                label: 'timing',
                name: 'timing',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /^[\d\.,:]+$/,
                      t('competition_edit.form.message.duration')
                    )
                    .max(10, t('competition_edit.form.message.max', { count: 10 })),
              },
              createCountry: {
                type: 'text',
                label: 'createCountry',
                name: 'createCountry',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(100, t('competition_edit.form.message.max', { count: 100 })),
              },
              about_ru: {
                type: INPUT_TYPES.textarea,
                label: 'about_ru',
                name: 'about_ru',
                placeholder: '',
                requiredLabel: true,
                maxLength: 1000,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /\S/,
                      t('competition_edit.form.message.no_space_symbol')
                    )
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              about_en: {
                type: INPUT_TYPES.textarea,
                label: 'about_en',
                name: 'about_en',
                placeholder: '',
                maxLength: 1000,
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(
                      /\S/,
                      t('competition_edit.form.message.no_space_symbol')
                    )
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              hasSubtitles: {
                type: 'radio',
                label: 'hasSubtitles',
                name: 'hasSubtitles',
                // checkboxLabel: 'Я принимаю условия использования и политику конфиденциальности',
                // requiredLabel: true,
                validation: yup
                  .boolean(),
                  // .oneOf([true], 'Необходимо согласиться с условиями'),
                  // .required('Это поле обязательно'),
                value: false,
              },
              cloudLink: {
                type: 'text',
                label: 'cloudLink',
                infoText: 'cloud_info',
                name: "cloudLink",
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    .test(
                      'is-cloud-link',
                      t('competition_edit.form.message.cloud_link'),
                      function (value) {
                        if (!value) return true;
                
                        const trimmed = value.trim();
                        return checkCloudLinks(trimmed);
                      }
                    ),
              },

              publishing: {
                type: 'text',
                label: 'publishing',
                name: 'publishing',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              author: {
                type: 'text',
                label: 'author',
                name: 'author',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              editor: {
                type: 'text',
                label: 'editor',
                name: 'editor',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              bookName: {
                type: 'text',
                label: 'bookName',
                name: 'bookName',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                    .string()
                    // .required('Обязательное поле')
                    // .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
                    .max(800, t('competition_edit.form.message.max', { count: 800 })),
              },
              bookYear: {
                type: 'text',
                label: 'bookYear',
                name: 'bookYear',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .test(
                      'bookYear',
                      t('competition_edit.form.message.year'),
                      function (value) {
                        if (!value) return true;
                
                        const trimmed = value.trim();
                        return /^\d{4}$/.test(trimmed)
                      }
                    )
                    .min(4, t('competition_edit.form.message.min', { count: 4 }))
                    .max(4, t('competition_edit.form.message.max', { count: 4 }))
              },
              webSite: {
                type: 'text',
                label: 'webSite',
                name: 'webSite',
                placeholder: 'https://example.com',
                requiredLabel: false,
                validation: yup
                  .string()
                  .test(
                    'is-pdf-link',
                    t('competition_edit.form.message.url_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkUrl(trimmed);
                    }
                  ),
              },
              contactPerson: {
                type: 'text',
                label: 'contactPerson',
                name: 'contactPerson',
                placeholder: '',
                requiredLabel: true,
                validation: yup
                    .string()
                    .required(t('competition_edit.form.message.required.required_field'))
                    .matches(/^[a-zA-Zа-яА-ЯёЁ\s-']+$/, t('competition_edit.form.message.only_word'))
              },
              pdfLink: {
                type: 'text',
                label: 'pdfLink',
                name: 'pdfLink',
                placeholder: '',
                requiredLabel: false,
                validation: yup
                  .string()
                  .test(
                    'is-pdf-link',
                    t('competition_edit.form.message.url_link'),
                    function (value) {
                      if (!value) return true;
              
                      const trimmed = value.trim();
                      return checkUrl(trimmed);
                    }
                  ),
              },

              ...extraConfig,
              ...consentConfig
            };
        } , [projectId, extraConfig, consentConfig, nominationId, language, t]),
        loading: configLoading
    }
}